import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './Plans.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Alert from '../../components/Alert/Alert';
import Loader from '../../components/Loader/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import CryptoJS from 'crypto-js';

export default function Plans() {
  const { token } = useParams();
  const [activePlanId, setActivePlanId] = useState(null);
  const [plan, setPlan] = useState(null); // solo un plan
  const [loading, setLoading] = useState(true);
  const [loadingPayment, setLoadingPayment] = useState(null);
  const [alertConfig, setAlertConfig] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch('https://api.minymol.com/subscriptions/mine', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const subscriptions = await res.json();
        const now = new Date();

        const activeSub = subscriptions.find(sub =>
          sub.status === 'active' &&
          new Date(sub.endDate) > now &&
          sub.plan?.id
        );

        if (activeSub) setActivePlanId(activeSub.plan.id);
      } catch (error) {
        console.error('Error al consultar suscripciones:', error);
      }
    };

    fetchSubscriptions();
  }, []);

  useEffect(() => {
    const fetchEncryptedPlan = async () => {
      if (!token) return;

      try {
        const bytes = CryptoJS.AES.decrypt(decodeURIComponent(token), 'clave-secreta');
        const decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        if (!decrypted.planId) throw new Error('Token inválido');

        const res = await fetch(`https://api.minymol.com/plans/${decrypted.planId}`);
        if (!res.ok) throw new Error('Error al obtener el plan');
        const data = await res.json();
        setPlan(data);
      } catch (error) {
        console.error('❌ Token inválido o expirado:', error);
        setAlertConfig({
          type: 'error',
          message: 'El enlace no es válido o ha expirado.',
          redirectTo: '/',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEncryptedPlan();
  }, [token]);

  useEffect(() => {
    if (location.pathname.includes('success')) {
      setAlertConfig({
        type: 'success',
        message: '✅ ¡Pago aprobado! Espera mientras revisamos tu documentación.',
        redirectTo: '/planes',
      });
    } else if (location.pathname.includes('pending')) {
      setAlertConfig({
        type: 'info',
        message: '⏳ Tu pago está en proceso. Te notificaremos cuando se confirme.',
      });
    } else if (location.pathname.includes('failure')) {
      setAlertConfig({
        type: 'error',
        message: '❌ El pago fue rechazado o cancelado. Intenta de nuevo.',
      });
    }
  }, [location.pathname]);

  const handleBuy = async (plan) => {
    try {
      setLoadingPayment(plan.id);

      // ✅ Detecta si es flujo manual por token
      const bytes = CryptoJS.AES.decrypt(decodeURIComponent(token), 'clave-secreta');
      const decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      const manualRequestId = decrypted.id;

      // Si hay manualRequestId, es un pago manual
      if (manualRequestId) {
        const res = await fetch(`https://api.minymol.com/manual-provider-requests/${manualRequestId}/pay`, {
          method: 'POST',
        });

        const data = await res.json();
        if (!res.ok || !data.init_point) throw new Error('Error generando enlace manual');

        localStorage.setItem('lastPaymentId', data.paymentId);
        window.location.href = data.init_point;
        return;
      }

      // ⚠️ Si no es manual, usa flujo normal con token
      const tokenAuth = localStorage.getItem('token');
      const res = await fetch('https://api.minymol.com/payments/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenAuth}`,
        },
        body: JSON.stringify({
          planId: plan.id,
          amount: plan.price,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.init_point) throw new Error('Error generando enlace de pago');

      localStorage.setItem('lastPaymentId', data.paymentId);
      window.location.href = data.init_point;
    } catch (error) {
      console.error('❌ Error al procesar el pago:', error);
      setAlertConfig({
        type: 'error',
        message: 'No se pudo generar el enlace de pago. Intenta más tarde.',
      });
    } finally {
      setLoadingPayment(null);
    }
  };


  if (loading) return <Loader />;

  return (
    <div style={{ backgroundColor: 'whitesmoke' }}>
      <Header minimal={true} />
      {alertConfig && (
        <Alert
          type={alertConfig.type}
          message={alertConfig.message}
          onClose={() => setAlertConfig(null)}
          redirectTo={alertConfig.redirectTo}
        />
      )}

      {plan ? (
        <div className="plans-container">
          <div className="plan-card">
            {plan.name.toLowerCase() === 'premium' && (
              <div className="plan-badge">Popular</div>
            )}
            <div className="plan-content">
              <h2 className="plan-title">{plan.name}</h2>
              <div className="plan-price">
                <span className="amount">${Number(plan.price).toFixed(2)}</span>
                <span className="per-month">/mes</span>
              </div>
              <ul className="plan-features">
                {plan.features?.map((feature, i) => (
                  <li key={i} className="plan-feature-item">
                    <FontAwesomeIcon className='check-icon' icon={faCheck} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                className={`plan-button ${plan.name.toLowerCase() === 'premium' ? 'primary' : 'outline'}`}
                onClick={() => handleBuy(plan)}
                disabled={loadingPayment === plan.id || activePlanId === plan.id}
              >
                {loadingPayment === plan.id ? 'Redirigiendo...' : 'Elegir Plan'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="plans-intro">
          <h2>No se pudo cargar el plan.</h2>
        </div>
      )}

      <Footer />
    </div>
  );
}